import { GetApp } from '@mui/icons-material';
import { Checkbox, CircularProgress, FormControlLabel, MenuItem, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import Button from '../../common/button';
import FlexContainer from '../../common/flex-container';
import Select from '../../common/select';
import TextField from '../../common/textfield';
import NameSwitcher from '../../common/name-switcher';
import SelectTokens from '../../tokens/select-tokens';
import type TokenMetadataItem from '../../tokens/types/token-metadata-item.ts';
import { getNftCollectionMetadata } from '../api';
import OwnerAddressFormat from '../enums/owner-address-format.ts';
import nftRewardCollectionMetadataMapped from '../mapping/nft-reward-collection-metadata-mapped.ts';
import type NftCollectionMetadata from '../types/nft-collection-metadata.ts';
import styles from './index.module.css';
import PanelTitle from '../../common/panel-title';
import Label from '../../common/label';
import {
  AddressTitle,
  AmountTitle,
  CollectionAddressTitle,
  DistributeStrategyTitle,
  DistributeTitle,
  ExcludeNftSaleTitle,
  ExcludeOwnerCollectionTitle,
  FiltersTitle,
  InSaleTitle,
  MessageTitle,
  OwnerTitle,
  ResultTitle,
  SaveTitle,
  TokenTitle,
  ViewTitle,
} from '../../formatted-messages';
import Locale from '../../enums/locale.ts';
import { RootContext } from '../../../main.tsx';

const INCLUDE_TITLE_EN = 'include';
const EXCLUDE_TITLE_EN = 'exclude';
const INCLUDE_TITLE_RU = 'включать';
const EXCLUDE_TITLE_RU = 'исключать';

type NftRewardFormProps = {
  onInputChange: (name: string, value: string | boolean | undefined) => void;
  collectionAddress: string | undefined;
  distributeAmount: string | undefined;
  distributeToken: string | undefined;
  distributeMessage: string | undefined;
  distributeStrategy: string;
  ownerAddressFormat: string;
  onNftRewardReportDownload: (format: string) => void;
  isLoading: boolean;
  isExcludeOwnerCollection: boolean;
  isExcludeNftSale: boolean;
  tokensSymbols: string[];
  tokensMetadata: TokenMetadataItem[];
};

const NftRewardForm: React.FunctionComponent<NftRewardFormProps> = (props: NftRewardFormProps) => {
  const context: any = useContext(RootContext);

  const collectionAddress: string | undefined = props.collectionAddress;
  const distributeAmount: string | undefined = props.distributeAmount;
  const distributeToken: string | undefined = props.distributeToken;
  const distributeMessage: string | undefined = props.distributeMessage;
  const isExcludeOwnerCollection: boolean = props.isExcludeOwnerCollection;
  const isExcludeNftSale: boolean = props.isExcludeNftSale;
  const distributeStrategy: string = props.distributeStrategy;

  const ntfCollectionMetadaatResult = useQuery({
    queryKey: ['nft-reward-collection'],
    queryFn: () => {
      if (collectionAddress) {
        return getNftCollectionMetadata(collectionAddress);
      }
    },
    enabled: false,
  });

  const { data: nftRewardCollectionMetadataData, isLoading }: { data: any; isLoading: boolean } =
    ntfCollectionMetadaatResult;

  const nftMetadataResult: NftCollectionMetadata = nftRewardCollectionMetadataMapped(
    nftRewardCollectionMetadataData?.data,
  );

  useEffect(() => {
    if (collectionAddress) {
      ntfCollectionMetadaatResult.refetch();
    }
  }, [collectionAddress]);

  const onCollectionAddressInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const element = evt.target as HTMLInputElement;
    props.onInputChange(element.name, element.value);
  };

  const onCollectionAddressSelectChange = (option: any) => {
    props.onInputChange(option.name, option?.value?.symbol);
  };

  const onOwnerCollectAddress = (name: string, value: string) => {
    props.onInputChange(name, value);
  };

  const onDistributeStrategyChange = (evt: any) => {
    const element = evt.target as HTMLInputElement;
    props.onInputChange(element.name, element.value);
  };

  const isValidForm: boolean = !!collectionAddress && !!distributeAmount && !!distributeToken;

  const onReportDownloadCsv = () => {
    props.onNftRewardReportDownload('csv');
  };

  const onReportDownloadMarkdown = () => {
    props.onNftRewardReportDownload('markdown');
  };

  const onExcludeOwnerCollectionModeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const element = evt.target as HTMLInputElement;
    props.onInputChange(element.name, element.checked);
  };

  const onExcludeOwnerCollectionModeMobileChange = (name: string, value: string) => {
    props.onInputChange(
      name,
      context.locale === Locale.RU ? value === EXCLUDE_TITLE_RU : value === EXCLUDE_TITLE_EN,
    );
  };

  const onExcludeNftSaleModeChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const element = evt.target as HTMLInputElement;
    props.onInputChange(element.name, element.checked);
  };

  const filterOwnerValue: string = isExcludeOwnerCollection
    ? context.locale === Locale.RU
      ? EXCLUDE_TITLE_RU
      : EXCLUDE_TITLE_EN
    : context.locale === Locale.RU
      ? INCLUDE_TITLE_RU
      : INCLUDE_TITLE_EN;
  const nftSaleValue: string = isExcludeNftSale
    ? context.locale === Locale.RU
      ? EXCLUDE_TITLE_RU
      : EXCLUDE_TITLE_EN
    : context.locale === Locale.RU
      ? INCLUDE_TITLE_RU
      : INCLUDE_TITLE_EN;

  const includeTitle: string = context.locale === Locale.RU ? INCLUDE_TITLE_RU : INCLUDE_TITLE_EN;

  const excludeTitle: string = context.locale === Locale.RU ? EXCLUDE_TITLE_RU : EXCLUDE_TITLE_EN;

  return (
    <form className={styles.form}>
      <FlexContainer className={styles['form-img-widget-container']}>
        <div className={styles['nft-reward-form-container']}>
          <FlexContainer gap={15} flexGrow={1} alignitems="center">
            {nftMetadataResult.imagePreview && (
              <FlexContainer
                width={100}
                alignitems="center"
                justifycontent="center"
                flexdirection="column"
                gap={10}
                alignself="flex-start"
              >
                {isLoading && <CircularProgress />}
                {nftMetadataResult.name && (
                  <div className={styles['form-img-container']}>
                    <p className={styles['form-img-name']}>{nftMetadataResult.name}</p>
                    {nftMetadataResult?.imagePreview && (
                      <img
                        src={nftMetadataResult.imagePreview}
                        alt={nftMetadataResult.name}
                        width="100"
                        height="100"
                      />
                    )}
                  </div>
                )}
              </FlexContainer>
            )}
            <div className={styles['input-collection-address-container']}>
              <p className={styles['form-img-name--mobile']}>{nftMetadataResult.name}</p>
              <TextField
                label={<CollectionAddressTitle />}
                id="collectionAddress"
                name="collectionAddress"
                variant="outlined"
                onChange={onCollectionAddressInputChange}
                value={collectionAddress}
                isError={!collectionAddress}
              />
            </div>
          </FlexContainer>
          <FlexContainer width="98%" alignitems="center" gap={20} margin="20px 0 0 0">
            <div className={styles['filters-container-desktop']}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isExcludeOwnerCollection"
                    onChange={onExcludeOwnerCollectionModeChange}
                    checked={isExcludeOwnerCollection}
                  />
                }
                label={<ExcludeOwnerCollectionTitle />}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="isExcludeNftSale"
                    onChange={onExcludeNftSaleModeChange}
                    checked={isExcludeNftSale}
                  />
                }
                label={<ExcludeNftSaleTitle />}
              />
            </div>
            <div className={styles['filters-container-mobile']}>
              <PanelTitle>
                <FiltersTitle />
              </PanelTitle>
              <div className={styles['form-element-container']}>
                <Label className={styles['form-element-label']} htmlFor="is-exclude-owner-collection">
                  <OwnerTitle />
                </Label>
                <NameSwitcher
                  disabled={props.isLoading}
                  id="is-exclude-owner-collection"
                  name="isExcludeOwnerCollection"
                  value={filterOwnerValue}
                  labelActive={excludeTitle}
                  labelInactive={includeTitle}
                  onChange={onExcludeOwnerCollectionModeMobileChange}
                />
              </div>
              <div className={styles['form-element-container']}>
                <Label className={styles['form-element-label']} htmlFor="is-exclude-nft-sale">
                  <InSaleTitle />
                </Label>
                <NameSwitcher
                  disabled={props.isLoading}
                  id="is-exclude-nft-sale"
                  name="isExcludeNftSale"
                  value={nftSaleValue}
                  labelActive={excludeTitle}
                  labelInactive={includeTitle}
                  onChange={onExcludeOwnerCollectionModeMobileChange}
                />
              </div>
            </div>
          </FlexContainer>
        </div>
        <div className={styles['distribute-container-desktop']}>
          <PanelTitle>
            <DistributeTitle />
          </PanelTitle>
          <div className={styles['form-element-container-select']}>
            <Select
              width="100%"
              name="distributeStrategy"
              label={<DistributeStrategyTitle />}
              value={distributeStrategy}
              onChange={onDistributeStrategyChange}
            >
              <MenuItem value={' '}>None</MenuItem>
              <MenuItem value="SplitForAll">Split for all</MenuItem>
              <MenuItem value="ForOneWeight">For one weight</MenuItem>
              <MenuItem value="ForOneAddress">For one address</MenuItem>
            </Select>
          </div>
          <FlexContainer width="initial">
            <TextField
              label={<AmountTitle />}
              id="distributeAmount"
              name="distributeAmount"
              variant="outlined"
              onChange={onCollectionAddressInputChange}
              value={distributeAmount}
              isError={!distributeAmount}
              textalign="center"
            />
          </FlexContainer>
          <div className={styles['form-element-container-select']}>
            {props.tokensMetadata.length > 0 ? (
              <SelectTokens
                id="distribute-token"
                name="distributeToken"
                activeElement={distributeToken}
                tokensMetadata={props.tokensMetadata}
                tokensSymbols={props.tokensSymbols}
                onSelectChange={onCollectionAddressSelectChange}
              />
            ) : (
              <TextField
                label={<TokenTitle />}
                id="distribute-token"
                name="distributeToken"
                variant="outlined"
                onChange={onCollectionAddressInputChange}
                value={distributeToken}
                isError={!distributeToken}
              />
            )}
          </div>
          <div className={styles['form-element-container-select']}>
            <TextField
              label={<MessageTitle />}
              id="distributeMessage"
              name="distributeMessage"
              variant="outlined"
              onChange={onCollectionAddressInputChange}
              value={distributeMessage}
              isError={!distributeMessage}
            />
          </div>
        </div>
      </FlexContainer>
      <FlexContainer className={styles['form-container']} gap={20} margin="20px 0 0 0">
        <div className={styles['distribute-container-desktop']}>
          <PanelTitle>
            <ViewTitle />
          </PanelTitle>
          <div className={styles['form-element-container']}>
            <Label className={styles['form-element-label']} htmlFor="owner-collect-address">
              <AddressTitle />
            </Label>
            <FlexContainer justifycontent="flex-start" alignitems="center" margin="0 0 0 10px">
              <NameSwitcher
                disabled={props.isLoading}
                id="owner-collect-address"
                name="ownerAddressFormat"
                value={props.ownerAddressFormat}
                labelActive={OwnerAddressFormat.Name}
                labelInactive={OwnerAddressFormat.Base64}
                labelInactive2={OwnerAddressFormat.Raw}
                onChange={onOwnerCollectAddress}
                width="120px"
                fontSize={14}
              />
            </FlexContainer>
          </div>
          <div className={styles['result-container-desktop']}>
            <Tooltip title="Download report">
              <Button
                type="button"
                variant="outlined"
                onClick={onReportDownloadCsv}
                disabled={!isValidForm || props.isLoading}
                fontSize="10px"
              >
                <GetApp /> Tonkeeper CSV
              </Button>
            </Tooltip>
            <Tooltip title="Download report">
              <Button
                type="button"
                variant="outlined"
                onClick={onReportDownloadMarkdown}
                disabled={!isValidForm || props.isLoading}
                fontSize="10px"
              >
                <GetApp /> Markdown
              </Button>
            </Tooltip>
          </div>
        </div>
        <div className={styles['result-container-mobile']}>
          <PanelTitle>
            <ResultTitle />
          </PanelTitle>
          <div className={styles['form-element-container']}>
            <Label className={styles['form-element-label']} htmlFor="owner-collect-address">
              Tonkeeper CSV
            </Label>
            <Tooltip title="Download report">
              <Button
                type="button"
                variant="outlined"
                onClick={onReportDownloadCsv}
                disabled={!isValidForm || props.isLoading}
                fontSize="10px"
              >
                <GetApp /> <SaveTitle />
              </Button>
            </Tooltip>
          </div>
          <div className={styles['form-element-container']}>
            <Label className={styles['form-element-label']} htmlFor="owner-collect-address">
              Tonkeeper CSV
            </Label>
            <Tooltip title="Download report">
              <Button
                type="button"
                variant="outlined"
                onClick={onReportDownloadMarkdown}
                disabled={!isValidForm || props.isLoading}
                fontSize="10px"
              >
                <GetApp /> <SaveTitle />
              </Button>
            </Tooltip>
          </div>
        </div>
      </FlexContainer>
    </form>
  );
};

export default NftRewardForm;
